// src/pages/provider/ProviderDashboard.jsx
import React, { useState, useEffect } from 'react';
import { 
  Bell, 
  Calendar, 
  DollarSign, 
  Users, 
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Star,
  MessageCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { Link } from 'react-router-dom';

export const ProviderDashboard = () => {
  const { userProfile } = useAuth();
  const { stats, getActionRequiredNotifications, getUnreadNotifications } = useNotifications();
  const [dashboardStats, setDashboardStats] = useState({
    totalBookings: 0,
    pendingBookings: 0,
    completedBookings: 0,
    totalRevenue: 0,
    avgRating: 0,
    responseRate: 0
  });

  const actionRequiredNotifications = getActionRequiredNotifications();
  const unreadNotifications = getUnreadNotifications();

  // Mock data - replace with actual API calls
  useEffect(() => {
    // Load provider dashboard stats
    setDashboardStats({
      totalBookings: 127,
      pendingBookings: 8,
      completedBookings: 119,
      totalRevenue: 48750,
      avgRating: 4.8,
      responseRate: 94
    });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back, {userProfile?.displayName || 'Provider'}!
          </h1>
          <p className="text-gray-600">
            Here's what's happening with your services today.
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Bookings</p>
                <p className="text-2xl font-bold text-gray-900">{dashboardStats.totalBookings}</p>
              </div>
              <Calendar className="h-8 w-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-orange-600">{dashboardStats.pendingBookings}</p>
              </div>
              <Clock className="h-8 w-8 text-orange-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Revenue</p>
                <p className="text-2xl font-bold text-green-600">
                  R{dashboardStats.totalRevenue.toLocaleString()}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Rating</p>
                <p className="text-2xl font-bold text-yellow-600">{dashboardStats.avgRating}</p>
              </div>
              <Star className="h-8 w-8 text-yellow-500" />
            </div>
          </div>
        </div>

        {/* Notifications Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Action Required */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Action Required</h3>
              <AlertCircle className="h-5 w-5 text-orange-500" />
            </div>
            <div className="text-3xl font-bold text-orange-600 mb-2">
              {actionRequiredNotifications.length}
            </div>
            <p className="text-sm text-gray-600 mb-4">
              New requests need your response
            </p>
            <Link
              to="/provider/notifications?filter=action_required"
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              View all →
            </Link>
          </div>

          {/* Unread Notifications */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Unread</h3>
              <Bell className="h-5 w-5 text-blue-500" />
            </div>
            <div className="text-3xl font-bold text-blue-600 mb-2">
              {unreadNotifications.length}
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Unread notifications
            </p>
            <Link
              to="/provider/notifications?filter=unread"
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              View all →
            </Link>
          </div>

          {/* Response Rate */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Response Rate</h3>
              <MessageCircle className="h-5 w-5 text-green-500" />
            </div>
            <div className="text-3xl font-bold text-green-600 mb-2">
              {dashboardStats.responseRate}%
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Average response time: 2.1 hours
            </p>
            <Link
              to="/provider/analytics"
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              View details →
            </Link>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Notifications */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Notifications</h3>
            {unreadNotifications.slice(0, 5).map(notification => (
              <div key={notification.id} className="flex items-center gap-3 py-3 border-b border-gray-100 last:border-b-0">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                  <p className="text-xs text-gray-600">{notification.message}</p>
                </div>
                <span className="text-xs text-gray-500">
                  {new Date(notification.createdAt).toLocaleDateString()}
                </span>
              </div>
            ))}
            {unreadNotifications.length === 0 && (
              <p className="text-gray-500 text-sm">No new notifications</p>
            )}
            <Link
              to="/provider/notifications"
              className="inline-block mt-4 text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              View all notifications →
            </Link>
          </div>

          {/* Quick Actions */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Link
                to="/provider/profile"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Users className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="font-medium text-gray-900">Update Profile</p>
                  <p className="text-sm text-gray-600">Manage your service details</p>
                </div>
              </Link>
              
              <Link
                to="/provider/availability"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Calendar className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="font-medium text-gray-900">Manage Availability</p>
                  <p className="text-sm text-gray-600">Update your calendar</p>
                </div>
              </Link>
              
              <Link
                to="/provider/analytics"
                className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <TrendingUp className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="font-medium text-gray-900">View Analytics</p>
                  <p className="text-sm text-gray-600">Performance insights</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};